import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Building2, ShieldCheck, Key, Bell, Users, Activity, TrendingUp, AlertOctagon, Globe, Loader2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import {
  VLKPageHeader, VLKCard, VLKKPICard, VLKButton, VLKStatusBadge, VLKTabs, VLKRiskMeter,
  VLKUserAvatar,
} from "@/components/vlk";
import { severityChip } from "@/components/liveAudit/audit-utils";

export default function InstitutionDetail() {
  const { vlk_inst_id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  const { data: inst, isLoading, isError } = useQuery({
    queryKey: ["institution", vlk_inst_id],
    queryFn: () => api.get(`/institutions/${vlk_inst_id}`).then((r) => r.data),
  });

  if (isLoading) {
    return (
      <div className="p-10 flex items-center justify-center text-muted-foreground text-sm">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading institution…
      </div>
    );
  }
  if (isError || !inst) return <div className="p-10 text-sm text-muted-foreground">Institution not found.</div>;

  return (
    <div data-testid="institution-detail-page">
      <VLKButton variant="ghost" size="sm" onClick={() => navigate("/institutions")} className="-ml-2 mb-3" data-testid="institution-back">
        <ArrowLeft className="h-4 w-4" /> Back to institutions
      </VLKButton>

      <VLKPageHeader
        title={inst.name}
        description={
          <div className="flex items-center gap-3 flex-wrap mt-1">
            <span className="font-mono text-xs text-muted-foreground">{inst.vlk_inst_id}</span>
            <VLKStatusBadge status={String(inst.status).toLowerCase()}>{inst.status}</VLKStatusBadge>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{inst.type}</span>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Globe className="h-3 w-3" /> {inst.country}</span>
            <span className="text-xs font-mono text-muted-foreground">{inst.primary_contact_email}</span>
          </div>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <VLKKPICard testId="inst-kpi-verif" label="Verifications" value={inst.verifications_count?.toLocaleString() ?? "0"} icon={ShieldCheck} />
        <VLKKPICard testId="inst-kpi-creds-active" label="Active creds" value={inst.counters.credentials_active} icon={Key} />
        <VLKKPICard testId="inst-kpi-creds-revoked" label="Revoked" value={inst.counters.credentials_revoked} tone="danger" icon={Key} />
        <VLKKPICard testId="inst-kpi-webhooks" label="Webhooks" value={inst.counters.webhooks} icon={Bell} />
        <VLKKPICard testId="inst-kpi-admins" label="Admins" value={inst.counters.admins} icon={Users} />
        <VLKKPICard testId="inst-kpi-open-cases" label="Open cases" value={inst.counters.open_cases} tone={inst.counters.open_cases > 0 ? "warning" : "default"} icon={AlertOctagon} />
      </div>

      <VLKCard>
        <div className="px-5 pt-4">
          <VLKTabs
            value={tab}
            onChange={setTab}
            items={[
              { value: "overview", label: "Overview" },
              { value: "credentials", label: `Credentials (${inst.credentials.length})` },
              { value: "webhooks", label: `Webhooks (${inst.webhooks.length})` },
              { value: "admins", label: `Admins (${inst.admins.length})` },
              { value: "audit", label: `Recent audit (${inst.recent_audit.length})` },
            ]}
          />
        </div>
        <div className="p-5">
          {tab === "overview" && <OverviewTab inst={inst} />}
          {tab === "credentials" && <CredentialsTab items={inst.credentials} />}
          {tab === "webhooks" && <WebhooksTab items={inst.webhooks} />}
          {tab === "admins" && <AdminsTab items={inst.admins} />}
          {tab === "audit" && <AuditTab items={inst.recent_audit} />}
        </div>
      </VLKCard>
    </div>
  );
}

/* ---------------------------- Tabs ---------------------------- */

function OverviewTab({ inst }) {
  const usage = inst.usage_24h;
  const chartData = useMemo(() => usage.buckets.map((b) => ({ hour: b.hour, calls: b.calls, errors: b.errors })), [usage]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      <div className="md:col-span-2 border border-border rounded p-4 bg-secondary/10">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">API traffic · last 24h</div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis dataKey="hour" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} />
              <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                cursor={{ fill: "hsl(var(--secondary) / 0.6)" }}
              />
              <Bar dataKey="calls" fill="hsl(var(--info))" />
              <Bar dataKey="errors" fill="hsl(var(--destructive))" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-2 text-xs">
          <MetricInline label="Total calls" value={usage.total_calls.toLocaleString()} />
          <MetricInline label="Errors" value={usage.total_errors.toLocaleString()} />
          <MetricInline label="Error rate" value={`${(usage.error_rate * 100).toFixed(2)}%`} />
        </div>
      </div>

      <div className="border border-border rounded p-4 bg-secondary/10">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Profile</div>
        <dl className="space-y-2 text-xs">
          <ProfileRow label="Country">{inst.country}</ProfileRow>
          <ProfileRow label="Type">{inst.type}</ProfileRow>
          <ProfileRow label="Status">{inst.status}</ProfileRow>
          <ProfileRow label="Contact"><span className="font-mono">{inst.primary_contact_email}</span></ProfileRow>
          <ProfileRow label="Created">{inst.created_at ? new Date(inst.created_at).toLocaleDateString() : "—"}</ProfileRow>
        </dl>
      </div>
    </div>
  );
}

function MetricInline({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-mono text-sm tabular mt-0.5">{value}</div>
    </div>
  );
}

function ProfileRow({ label, children }) {
  return (
    <div className="grid grid-cols-[80px_1fr] gap-3">
      <dt className="text-[10px] uppercase tracking-widest text-muted-foreground pt-0.5">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function CredentialsTab({ items }) {
  if (!items.length) return <Empty label="No credentials issued." />;
  return (
    <table className="w-full text-sm">
      <thead className="bg-muted/40 border-b border-border">
        <tr>
          {["Name", "Env", "Prefix", "Scopes", "Status", "Created"].map((h) => (
            <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((c) => (
          <tr key={c.credential_id} data-testid={`inst-cred-${c.credential_id}`} className="border-b border-border last:border-0 hover:bg-secondary/40 font-mono text-xs">
            <td className="px-3 py-2 font-medium normal-case">{c.name}</td>
            <td className="px-3 py-2 uppercase tracking-widest text-[10px] text-muted-foreground">{c.env}</td>
            <td className="px-3 py-2">{c.key_prefix}</td>
            <td className="px-3 py-2 text-muted-foreground">{(c.scopes || []).join(", ") || "—"}</td>
            <td className="px-3 py-2"><VLKStatusBadge status={String(c.status).toLowerCase()}>{c.status}</VLKStatusBadge></td>
            <td className="px-3 py-2 text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function WebhooksTab({ items }) {
  if (!items.length) return <Empty label="No webhooks configured." />;
  return (
    <table className="w-full text-sm">
      <thead className="bg-muted/40 border-b border-border">
        <tr>
          {["URL", "Events", "Status", "Last delivery"].map((h) => (
            <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((w) => (
          <tr key={w.webhook_id} className="border-b border-border last:border-0 font-mono text-xs">
            <td className="px-3 py-2 font-medium truncate max-w-[260px]">{w.url}</td>
            <td className="px-3 py-2 text-muted-foreground">{(w.events || []).join(", ")}</td>
            <td className="px-3 py-2"><VLKStatusBadge status={String(w.status).toLowerCase()}>{w.status}</VLKStatusBadge></td>
            <td className="px-3 py-2 text-muted-foreground">{w.last_delivery_at ? new Date(w.last_delivery_at).toLocaleString() : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AdminsTab({ items }) {
  if (!items.length) return <Empty label="No assigned admins." />;
  return (
    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {items.map((u) => (
        <li key={u.user_id} className="border border-border rounded-sm p-3 flex items-center gap-3">
          <VLKUserAvatar name={u.name} src={u.avatar_url} size={36} />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">{u.name}</div>
            <div className="text-[11px] text-muted-foreground font-mono truncate">{u.email}</div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{u.role}</div>
        </li>
      ))}
    </ul>
  );
}

function AuditTab({ items }) {
  if (!items.length) return <Empty label="No recent audit events for this institution." />;
  return (
    <table className="w-full text-sm">
      <thead className="bg-muted/40 border-b border-border">
        <tr>
          {["When", "Action", "Actor", "Risk", "Outcome"].map((h) => (
            <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((a) => (
          <tr key={a.ledger_id} className="border-b border-border last:border-0 font-mono text-xs">
            <td className="px-3 py-2 text-muted-foreground">{new Date(a.timestamp).toLocaleString()}</td>
            <td className="px-3 py-2 font-medium">{a.action}</td>
            <td className="px-3 py-2 text-muted-foreground">{a.actor_email}</td>
            <td className="px-3 py-2"><VLKRiskMeter level={a.risk_level} /></td>
            <td className="px-3 py-2 uppercase text-[10px] tracking-widest">
              <span className={a.outcome === "SUCCESS" ? "text-success" : "text-destructive"}>{a.outcome}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Empty({ label }) {
  return <div className="text-sm text-muted-foreground p-6 text-center">{label}</div>;
}
